import {useEffect, useMemo} from 'react';

import isEqual from 'lodash/isEqual';
import {productFruits} from 'product-fruits';
import {useSelector} from 'react-redux';

import {getAccessLevel} from 'client/services/cookie-data-source';

import {selectClientUser, selectClientUserMembership} from 'client/ducks/user-clients/selectors';
import {selectUser} from 'client/ducks/user/selectors';
import {selectViewMode} from 'client/ducks/user/selectors';

import {PRODUCT_FRUITS_WORKSPACE_CODE, USER_TYPES} from 'client/common/config';

import useFetchSelectedAgencyClient from 'client/components/agencies/hooks/useFetchSelectedAgencyClient';
import {AccessLevelType} from 'client/models/common';
import {operationHasConfigTab} from 'client/models/operations/helpers';

let isInitialized = false;
let prevUserData = {};

const initProductFruit = (locale: string, userData: any) => {
  if (PRODUCT_FRUITS_WORKSPACE_CODE) {
    productFruits.init(PRODUCT_FRUITS_WORKSPACE_CODE, locale, userData);
  }
};

const updateProductFruit = (locale: string, userData: any) => {
  // @ts-expect-error Product Fruits adds this property to window at runtime
  const identifyUser = window.productFruits?.identifyUser;
  // @ts-expect-error Product Fruits adds this property to window at runtime
  const destroyPf = window.productFruits?.services?.destroy;

  if (identifyUser) {
    identifyUser(userData);
  } else {
    destroyPf?.();
    initProductFruit(locale, userData);
  }
};

export default (params: {clientId: number}) => {
  const {clientId} = params;
  const defaultUser = useSelector(selectUser);
  const {client_user: clientUser = {}} = useSelector(selectClientUser);
  const viewMode = useSelector(selectViewMode);

  const user = useMemo(() => {
    return viewMode.on ? clientUser : defaultUser;
  }, [viewMode.on, clientUser, defaultUser]);

  const {loading, generalClient, agencyClient} = useFetchSelectedAgencyClient(clientId);
  const clientUserMembership = useSelector(selectClientUserMembership);
  const accessLevel = getAccessLevel() as AccessLevelType;

  const isEditor = useMemo((): boolean | null => {
    try {
      if (loading) {
        return null;
      }
      const client = agencyClient || generalClient;
      const showCompanyElements = !generalClient?.isAgency || Boolean(agencyClient);
      return (
        operationHasConfigTab({
          client,
          accessLevel,
          membershipIsEditor: clientUserMembership?.editor,
          viewUserId: viewMode.id,
        }) && showCompanyElements
      );
    } catch {
      console.error('PF: Cannot determine user role');
      return null;
    }
  }, [agencyClient, clientUserMembership?.editor, generalClient, loading, viewMode.id, accessLevel]);

  useEffect(() => {
    try {
      if (
        PRODUCT_FRUITS_WORKSPACE_CODE &&
        user.id &&
        user.type === USER_TYPES.CLIENT_USER &&
        user.fruit_product_key &&
        isEditor !== null
      ) {
        const diyStatus = isEditor ? 'editor' : 'viewer';
        const userData = {
          username: user.id.toString(),
          firstname: user.first_name,
          lastname: user.last_name,
          role: `${accessLevel} ${diyStatus}`,
          props: {
            access_level: accessLevel,
            diy_status: diyStatus,
          },
          hmac: {
            hash: user.fruit_product_key,
            expiration: user.fruit_product_key_expired_at,
          },
        };
        if (isEqual(userData, prevUserData)) {
          return;
        } else if (isInitialized) {
          updateProductFruit(user.locale, userData);
        } else {
          initProductFruit(user.locale, userData);
          isInitialized = true;
        }
        prevUserData = userData;
      }
    } catch {
      console.error('PF: Cannot initialize/update user data');
    }
  }, [
    user.id,
    user.type,
    user.locale,
    user.first_name,
    user.last_name,
    user.fruit_product_key,
    user.fruit_product_key_expired_at,
    accessLevel,
    isEditor,
  ]);
};
